import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Apple,
  ArrowLeft,
  Beef,
  Calculator,
  Carrot,
  ClipboardPlus,
  Cookie,
  Milk,
  Plus,
  Save,
  Search,
  Trash2,
  Utensils,
  Wheat,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { filterFoods, FOOD_FILTERS, formatServing, getFoodIconKey, getSafeQuantityUnit } from "../lib/v2FoodUtils";
import {
  buildCustomFoodFromMixer,
  calculateMixerTotals,
  createFoodIngredient,
  createManualIngredient,
  scaleFoodNutrients,
  validateMixerQuantity,
} from "../lib/v2MixerUtils";
import { createV2CustomFood, loadV2FoodsUnified } from "../lib/v2Storage";

const iconMap = {
  food: Utensils,
  custom: Cookie,
  fruit: Apple,
  vegetable: Carrot,
  protein: Beef,
  grain: Wheat,
  dairy: Milk,
  meal: Utensils,
  manual: ClipboardPlus,
};

const initialManualForm = {
  name: "",
  category: "",
  quantity: "",
  quantity_unit: "serving",
  potassium_mg: "",
  phosphorus_mg: "",
  sodium_mg: "",
  protein_g: "",
  icon_key: "manual",
};

const initialResultForm = {
  name: "",
  name_ar: "",
  category: "Mixed meal",
  quantity: "1",
  quantity_unit: "serving",
  icon_key: "meal",
};

export default function V2Mixer() {
  const { lang } = useLang();
  const { isGuest } = useV2Session();
  const copy = getV2Copy(lang);
  const navigate = useNavigate();
  const [foods] = useState(() => loadV2FoodsUnified({ isGuest }));
  const [search, setSearch] = useState("");
  const [selectedFoodKey, setSelectedFoodKey] = useState("");
  const [selectedQuantity, setSelectedQuantity] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [manualForm, setManualForm] = useState(initialManualForm);
  const [manualErrors, setManualErrors] = useState({});
  const [resultForm, setResultForm] = useState(initialResultForm);
  const [resultErrors, setResultErrors] = useState({});
  const [saveError, setSaveError] = useState("");

  const foodOptions = useMemo(
    () => filterFoods(foods, { search, type: FOOD_FILTERS.all, category: "all" }).slice(0, 30),
    [foods, search]
  );
  const selectedFood = useMemo(
    () => foods.find((food) => foodKey(food) === selectedFoodKey) || null,
    [foods, selectedFoodKey]
  );
  const totals = useMemo(() => calculateMixerTotals(ingredients), [ingredients]);
  const hasIngredientErrors = ingredients.some((ingredient) => ingredient.quantity_error);

  const handleSelectedFoodChange = (value) => {
    const nextFood = foods.find((food) => foodKey(food) === value);
    setSelectedFoodKey(value);
    setSelectedQuantity(nextFood ? String(nextFood.quantity || nextFood.serving_quantity || 1) : "");
  };

  const handleAddFood = () => {
    const result = createFoodIngredient(selectedFood, selectedQuantity);
    if (!result.ok) {
      setSaveError(result.errors.quantity || result.errors.food || "Check the selected food details.");
      return;
    }

    setIngredients((current) => [{ ...result.ingredient, base_food: selectedFood }, ...current]);
    setSaveError("");
  };

  const updateIngredientQuantity = (ingredientId, value) => {
    setIngredients((current) =>
      current.map((ingredient) => {
        if (ingredient.id !== ingredientId) return ingredient;

        const validation = validateMixerQuantity(value);
        if (!validation.valid) {
          return { ...ingredient, quantity: value, quantity_error: validation.error };
        }

        return {
          ...ingredient,
          quantity: validation.quantity,
          quantity_error: "",
          ...scaleFoodNutrients(ingredient.base_food || ingredient, validation.quantity),
        };
      })
    );
  };

  const removeIngredient = (ingredientId) => {
    setIngredients((current) => current.filter((ingredient) => ingredient.id !== ingredientId));
  };

  const updateManualField = (field, value) => {
    setManualForm((current) => ({ ...current, [field]: value }));
    setManualErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleAddManualIngredient = () => {
    const result = createManualIngredient(manualForm);
    if (!result.ok) {
      setManualErrors(result.errors);
      return;
    }

    setIngredients((current) => [result.ingredient, ...current]);
    setManualForm(initialManualForm);
    setManualErrors({});
    setSaveError("");
  };

  const updateResultField = (field, value) => {
    setResultForm((current) => ({ ...current, [field]: value }));
    setResultErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSaveCustomFood = (event) => {
    event.preventDefault();

    if (ingredients.length === 0) {
      setSaveError("Add at least one ingredient before saving.");
      return;
    }

    if (hasIngredientErrors) {
      setSaveError("Fix ingredient quantities before saving.");
      return;
    }

    const payload = buildCustomFoodFromMixer(resultForm, ingredients);
    const result = createV2CustomFood(payload, { isGuest });
    if (!result.ok) {
      setResultErrors(result.errors);
      setSaveError("Check the food details before saving.");
      return;
    }

    toast.success("Custom food saved.");
    navigate("/foods");
  };

  return (
    <section className="mx-auto max-w-7xl space-y-6">
      <header className="space-y-3">
        <Button asChild variant="ghost" className="px-0">
          <Link to="/dashboard">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </Button>
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Calculator className="h-5 w-5" />
          </span>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-primary">{copy.brand}</p>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">Mixer</h1>
            <p className="max-w-3xl text-muted-foreground">
              Combine foods and measured ingredients, review nutrient totals, then save the result to your custom foods.
            </p>
          </div>
        </div>
      </header>

      <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <section className="space-y-5">
          <section className="rounded-lg border border-border bg-card p-5">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Search className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold">Add From Foods</h2>
                <p className="text-sm text-muted-foreground">Use verified foods and your custom foods.</p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1fr_1fr_0.65fr_auto] lg:items-end">
              <Field label="Search" htmlFor="mixer-search">
                <Input
                  id="mixer-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by name or category"
                />
              </Field>
              <Field label="Food" htmlFor="mixer-food">
                <select
                  id="mixer-food"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={selectedFoodKey}
                  onChange={(event) => handleSelectedFoodChange(event.target.value)}
                >
                  <option value="">Select food</option>
                  {foodOptions.map((food) => (
                    <option key={foodKey(food)} value={foodKey(food)}>
                      {food.display_name} - {food.category}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Quantity" htmlFor="mixer-selected-quantity">
                <Input
                  id="mixer-selected-quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={selectedQuantity}
                  onChange={(event) => setSelectedQuantity(event.target.value)}
                />
              </Field>
              <Button type="button" onClick={handleAddFood}>
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>

            {selectedFood && (
              <div className="mt-4 rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                Serving reference: {formatServing(selectedFood.quantity, selectedFood.quantity_unit)}. Nutrients are scaled by quantity.
              </div>
            )}
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ClipboardPlus className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold">Add Measured Ingredient</h2>
                <p className="text-sm text-muted-foreground">Enter nutrient values you have already measured.</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Ingredient name" htmlFor="manual-name" error={manualErrors.name}>
                <Input
                  id="manual-name"
                  value={manualForm.name}
                  onChange={(event) => updateManualField("name", event.target.value)}
                />
              </Field>
              <Field label="Category" htmlFor="manual-category">
                <Input
                  id="manual-category"
                  value={manualForm.category}
                  placeholder="Measured"
                  onChange={(event) => updateManualField("category", event.target.value)}
                />
              </Field>
              <Field label="Quantity" htmlFor="manual-quantity" error={manualErrors.quantity}>
                <NumberInput
                  id="manual-quantity"
                  value={manualForm.quantity}
                  onChange={(value) => updateManualField("quantity", value)}
                />
              </Field>
              <Field label="Unit" htmlFor="manual-unit" error={manualErrors.quantity_unit}>
                <Input
                  id="manual-unit"
                  value={manualForm.quantity_unit}
                  onChange={(event) => updateManualField("quantity_unit", event.target.value)}
                />
              </Field>
              <Field label="Potassium (mg)" htmlFor="manual-potassium" error={manualErrors.potassium_mg}>
                <NumberInput
                  id="manual-potassium"
                  value={manualForm.potassium_mg}
                  onChange={(value) => updateManualField("potassium_mg", value)}
                />
              </Field>
              <Field label="Phosphorus (mg)" htmlFor="manual-phosphorus" error={manualErrors.phosphorus_mg}>
                <NumberInput
                  id="manual-phosphorus"
                  value={manualForm.phosphorus_mg}
                  onChange={(value) => updateManualField("phosphorus_mg", value)}
                />
              </Field>
              <Field label="Sodium (mg)" htmlFor="manual-sodium" error={manualErrors.sodium_mg}>
                <NumberInput
                  id="manual-sodium"
                  value={manualForm.sodium_mg}
                  onChange={(value) => updateManualField("sodium_mg", value)}
                />
              </Field>
              <Field label="Protein (g)" htmlFor="manual-protein" error={manualErrors.protein_g}>
                <NumberInput
                  id="manual-protein"
                  value={manualForm.protein_g}
                  onChange={(value) => updateManualField("protein_g", value)}
                />
              </Field>
            </div>

            <div className="mt-4 flex justify-end">
              <Button type="button" variant="outline" onClick={handleAddManualIngredient}>
                <Plus className="h-4 w-4" />
                Add Measured Ingredient
              </Button>
            </div>
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-bold">Ingredients</h2>
            {ingredients.length > 0 ? (
              <div className="mt-4 space-y-3">
                {ingredients.map((ingredient) => (
                  <IngredientRow
                    key={ingredient.id}
                    ingredient={ingredient}
                    onQuantityChange={updateIngredientQuantity}
                    onRemove={removeIngredient}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-4 rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                Add foods or measured ingredients to calculate totals.
              </p>
            )}
          </section>
        </section>

        <aside className="space-y-5">
          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-bold">Calculated Totals</h2>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Nutrient label="Potassium" value={totals.potassium_mg} unit="mg" />
              <Nutrient label="Phosphorus" value={totals.phosphorus_mg} unit="mg" />
              <Nutrient label="Sodium" value={totals.sodium_mg} unit="mg" />
              <Nutrient label="Protein" value={totals.protein_g} unit="g" />
            </div>
          </section>

          <form className="rounded-lg border border-border bg-card p-5" onSubmit={handleSaveCustomFood}>
            <h2 className="text-lg font-bold">Save as Custom Food</h2>
            <div className="mt-5 space-y-4">
              <Field label="Food name" htmlFor="result-name" error={resultErrors.name}>
                <Input
                  id="result-name"
                  value={resultForm.name}
                  onChange={(event) => updateResultField("name", event.target.value)}
                />
              </Field>
              <Field label="Arabic name" htmlFor="result-name-ar">
                <Input
                  id="result-name-ar"
                  value={resultForm.name_ar}
                  onChange={(event) => updateResultField("name_ar", event.target.value)}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Category" htmlFor="result-category">
                  <Input
                    id="result-category"
                    value={resultForm.category}
                    onChange={(event) => updateResultField("category", event.target.value)}
                  />
                </Field>
                <Field label="Icon" htmlFor="result-icon">
                  <select
                    id="result-icon"
                    className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={resultForm.icon_key}
                    onChange={(event) => updateResultField("icon_key", event.target.value)}
                  >
                    <option value="meal">Meal</option>
                    <option value="custom">Custom</option>
                    <option value="grain">Grain</option>
                    <option value="protein">Protein</option>
                    <option value="vegetable">Vegetable</option>
                    <option value="dairy">Dairy</option>
                  </select>
                </Field>
                <Field label="Serving quantity" htmlFor="result-quantity" error={resultErrors.quantity}>
                  <NumberInput
                    id="result-quantity"
                    value={resultForm.quantity}
                    onChange={(value) => updateResultField("quantity", value)}
                  />
                </Field>
                <Field label="Serving unit" htmlFor="result-unit" error={resultErrors.quantity_unit}>
                  <Input
                    id="result-unit"
                    value={resultForm.quantity_unit}
                    onChange={(event) => updateResultField("quantity_unit", event.target.value)}
                  />
                </Field>
              </div>

              {saveError && <p className="rounded-lg bg-destructive/10 p-3 text-sm font-medium text-destructive">{saveError}</p>}

              <Button type="submit" className="w-full">
                <Save className="h-4 w-4" />
                Save Custom Food
              </Button>
            </div>
          </form>
        </aside>
      </div>
    </section>
  );
}

function IngredientRow({ ingredient, onQuantityChange, onRemove }) {
  const Icon = iconMap[getFoodIconKey(ingredient.icon_key)] || Utensils;
  const canScale = Boolean(ingredient.base_food);

  return (
    <article className="rounded-lg border border-border bg-background p-4">
      <div className="grid gap-4 lg:grid-cols-[1fr_150px_auto] lg:items-start">
        <div className="flex min-w-0 gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="truncate font-extrabold">{ingredient.name}</h3>
            <p className="text-sm text-muted-foreground">{ingredient.category || "Custom"} - {ingredient.source}</p>
          </div>
        </div>

        <Field label="Quantity" htmlFor={`ingredient-${ingredient.id}`}>
          <Input
            id={`ingredient-${ingredient.id}`}
            type="number"
            min="0"
            step="0.01"
            value={ingredient.quantity}
            disabled={!canScale}
            onChange={(event) => onQuantityChange(ingredient.id, event.target.value)}
          />
          {ingredient.quantity_error && <p className="text-sm font-medium text-destructive">{ingredient.quantity_error}</p>}
          <p className="text-xs text-muted-foreground">{getSafeQuantityUnit(ingredient.quantity_unit, "serving")}</p>
        </Field>

        <Button type="button" variant="ghost" className="text-destructive" onClick={() => onRemove(ingredient.id)}>
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Nutrient label="Potassium" value={ingredient.potassium_mg} unit="mg" />
        <Nutrient label="Phosphorus" value={ingredient.phosphorus_mg} unit="mg" />
        <Nutrient label="Sodium" value={ingredient.sodium_mg} unit="mg" />
        <Nutrient label="Protein" value={ingredient.protein_g} unit="g" />
      </div>
    </article>
  );
}

function Field({ label, htmlFor, error, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

function NumberInput({ id, value, onChange }) {
  return (
    <Input
      id={id}
      type="number"
      min="0"
      step="0.01"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function Nutrient({ label, value, unit }) {
  return (
    <div className="rounded-lg bg-muted px-3 py-2 text-sm">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <span className="font-bold">
        {formatAmount(value)} {unit}
      </span>
    </div>
  );
}

function foodKey(food) {
  return `${food.type || "food"}:${food.id}`;
}

function formatAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}
